import React, { useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { fmt, fmtUnit, fmtDate } from '../utils/helpers';
import { TableExportBar } from '../shared/index';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';
import InfoTip from './InfoTip';

const TH = { padding: '7px 10px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: '#fff', whiteSpace: 'nowrap', textAlign: 'center' };
const TD = { padding: '7px 10px', fontSize: '0.76rem', color: '#333', borderBottom: '1px solid #ede9e4', verticalAlign: 'middle', textAlign: 'right' };
const SEP = { borderRight: '2px solid #d4c8bc' };

const RATIO_INFO = 'Ratio = Encours / Ventes — part du montant vendu restant à encaisser.';

// Couleur par bloc — distingue visuellement titré / non titré / total
const BLOC_COLORS = { titre: '#1565c0', nonTitre: '#e65100', total: '#37474f' };

const BUCKET_LABELS  = { titre: 'Lots vendus titrés', nonTitre: 'Lots vendus non titrés', total: 'Total (titrés + non titrés)' };
const COLUMN_LABELS  = { ventes: 'Ventes', encaisse: 'Encaissements', encours: 'Encours', ratio: 'Ratio Encours / Ventes' };
const STATUT_LABELS  = { NOT_YET: 'Pas encore signé', CHEZ: 'Signé client', EN_COURS: 'Signature en cours', SIGNE: 'Signé notaire' };

// Complément — disponibilité du titre foncier (bien.disponibilite_tf, 3 valeurs réelles en base)
const TF_BUCKETS = ['oui', 'non', 'prochainement'];
const TF_LABELS  = { oui: 'Lots vendus titrés', non: 'Lots vendus non titrés', prochainement: 'Lots vendus Prochainement disponible', total: 'Total' };
const TF_COLORS  = { oui: '#2e7d32', non: '#607d8b', prochainement: '#e65100', total: '#37474f' };
const TF_BG      = { oui: '#e3f0e4', non: '#e7ebec', prochainement: '#fbe9dd' };
const TF_INFO     = "Disponibilité du titre foncier (bien.disponibilite_tf) : Titré = TF disponible · Non titré = TF non disponible · Prochainement = TF en cours d'obtention.";

// Pills filtre — statut notaire (même style que la section "Prochainement au Notaire")
const STATUT_PILLS = [
  { key: 'NOT_YET',  label: 'Pas encore signé' },
  { key: 'CHEZ',     label: 'Signé client' },
  { key: 'EN_COURS', label: 'Signature en cours' },
  { key: 'SIGNE',    label: 'Signé notaire' },
];

const pct = (num, den) => den > 0 ? Math.round((num / den) * 100) : null;

const blocCells = (bucket) => {
  const ventes   = bucket.ventes;
  const encaisse = bucket.encaisse;
  const encours  = ventes - encaisse;
  return { ventes, encaisse, encours, pctEncrs: pct(encours, ventes) };
};

// Vert si reliquat faible, orange si modéré, rouge si élevé
const ratioColor = (p) => p == null ? '#999' : p >= 60 ? '#c62828' : p >= 30 ? '#e65100' : '#2e7d32';

const BlocCells = ({ bucket, last, unit, onCellClick }) => {
  const c = blocCells(bucket);
  const rc = ratioColor(c.pctEncrs);
  const cur = onCellClick ? 'pointer' : 'default';
  const tip = onCellClick ? 'Voir le détail' : undefined;
  const click = (col) => onCellClick ? () => onCellClick(col) : undefined;
  return (<>
    <td style={{ ...TD, cursor: cur }} onClick={click('ventes')} title={tip}>{fmtUnit(c.ventes, unit)}</td>
    <td style={{ ...TD, color: '#2e7d32', cursor: cur }} onClick={click('encaisse')} title={tip}>{fmtUnit(c.encaisse, unit)}</td>
    <td style={{ ...TD, color: rc, fontWeight: 600, cursor: cur }} onClick={click('encours')} title={tip}>{fmtUnit(c.encours, unit)}</td>
    <td style={{ ...TD, color: rc, fontSize: '0.7rem', fontWeight: 700, cursor: cur, ...(last ? {} : SEP) }} onClick={click('ratio')} title={tip}>{c.pctEncrs != null ? `${c.pctEncrs}%` : '—'}</td>
  </>);
};

const SituationEncoursSection = ({ rows = [], loading, unit, fin }) => {
  const [activeStatuts, setActiveStatuts] = useState(() => new Set(['NOT_YET']));
  const [activeCell, setActiveCell] = useState(null); // { projetId, projet, bucketKey, column }

  const toggleStatut = (key) => {
    setActiveStatuts(prev => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  };

  const filtered = useMemo(() => {
    if (activeStatuts.size === 0) return rows;
    return rows.filter(r => activeStatuts.has(r.statutNotaire));
  }, [rows, activeStatuts]);

  // Agrégation par projet × titré / non titré
  const tableRows = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const k = r.projetId ?? r.projet;
      if (!map[k]) map[k] = {
        projetId: k, projet: r.projet || '—',
        titre:    { ventes: 0, encaisse: 0 },
        nonTitre: { ventes: 0, encaisse: 0 },
      };
      const bucket = r.titre ? map[k].titre : map[k].nonTitre;
      bucket.ventes   += r.prix;
      bucket.encaisse += r.encaisse;
    });
    return Object.values(map)
      .map(row => ({ ...row, total: { ventes: row.titre.ventes + row.nonTitre.ventes, encaisse: row.titre.encaisse + row.nonTitre.encaisse } }))
      .sort((a, b) => b.total.ventes - a.total.ventes);
  }, [filtered]);

  const grandTotal = useMemo(() => tableRows.reduce((acc, r) => ({
    titre:    { ventes: acc.titre.ventes    + r.titre.ventes,    encaisse: acc.titre.encaisse    + r.titre.encaisse },
    nonTitre: { ventes: acc.nonTitre.ventes + r.nonTitre.ventes, encaisse: acc.nonTitre.encaisse + r.nonTitre.encaisse },
    total:    { ventes: acc.total.ventes    + r.total.ventes,    encaisse: acc.total.encaisse    + r.total.encaisse },
  }), { titre: { ventes: 0, encaisse: 0 }, nonTitre: { ventes: 0, encaisse: 0 }, total: { ventes: 0, encaisse: 0 } }), [tableRows]);

  const totalCells = blocCells(grandTotal.total);

  // Agrégation complémentaire par projet × disponibilité du titre foncier (oui / non / prochainement)
  const tableRowsTf = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const k = r.projetId ?? r.projet;
      if (!map[k]) map[k] = {
        projetId: k, projet: r.projet || '—',
        oui: { ventes: 0, encaisse: 0 }, non: { ventes: 0, encaisse: 0 }, prochainement: { ventes: 0, encaisse: 0 },
      };
      const bucket = map[k][r.disponibiliteTf];
      if (bucket) { bucket.ventes += r.prix; bucket.encaisse += r.encaisse; }
    });
    return Object.values(map)
      .map(row => ({
        ...row,
        total: {
          ventes:   row.oui.ventes   + row.non.ventes   + row.prochainement.ventes,
          encaisse: row.oui.encaisse + row.non.encaisse + row.prochainement.encaisse,
        },
      }))
      .sort((a, b) => b.total.ventes - a.total.ventes);
  }, [filtered]);

  const grandTotalTf = useMemo(() => tableRowsTf.reduce((acc, r) => ({
    oui:           { ventes: acc.oui.ventes + r.oui.ventes,                     encaisse: acc.oui.encaisse + r.oui.encaisse },
    non:           { ventes: acc.non.ventes + r.non.ventes,                     encaisse: acc.non.encaisse + r.non.encaisse },
    prochainement: { ventes: acc.prochainement.ventes + r.prochainement.ventes, encaisse: acc.prochainement.encaisse + r.prochainement.encaisse },
    total:         { ventes: acc.total.ventes + r.total.ventes,                 encaisse: acc.total.encaisse + r.total.encaisse },
  }), { oui: { ventes: 0, encaisse: 0 }, non: { ventes: 0, encaisse: 0 }, prochainement: { ventes: 0, encaisse: 0 }, total: { ventes: 0, encaisse: 0 } }), [tableRowsTf]);

  const exportRowsTf = (list, fmtV = v => v) => list.map(r => {
    const cells = TF_BUCKETS.reduce((acc, k) => ({ ...acc, [k]: blocCells(r[k]) }), {});
    const tot = blocCells(r.total);
    const pctStr = v => v != null ? `${v}%` : '—';
    const row = { 'Projet': r.projet };
    TF_BUCKETS.forEach(k => {
      row[`Ventes (${TF_LABELS[k]})`] = fmtV(cells[k].ventes);
      row[`Encaissements (${TF_LABELS[k]})`] = fmtV(cells[k].encaisse);
      row[`Encours (${TF_LABELS[k]})`] = fmtV(cells[k].encours);
      row[`Ratio (${TF_LABELS[k]})`] = pctStr(cells[k].pctEncrs);
    });
    row['Ventes (total)'] = fmtV(tot.ventes); row['Encaissements (total)'] = fmtV(tot.encaisse);
    row['Encours (total)'] = fmtV(tot.encours); row['Ratio (total)'] = pctStr(tot.pctEncrs);
    return row;
  });

  // Lignes brutes derrière la cellule cliquée (même filtre statut que le tableau)
  const detailRows = useMemo(() => {
    if (!activeCell) return [];
    let base = filtered;
    if (activeCell.projetId !== 'ALL') base = base.filter(r => (r.projetId ?? r.projet) === activeCell.projetId);
    if (activeCell.dim === 'tf') {
      if (activeCell.bucketKey !== 'total') base = base.filter(r => r.disponibiliteTf === activeCell.bucketKey);
    } else {
      if (activeCell.bucketKey === 'titre')        base = base.filter(r => r.titre);
      else if (activeCell.bucketKey === 'nonTitre') base = base.filter(r => !r.titre);
    }
    return [...base].sort((a, b) => b.prix - a.prix);
  }, [activeCell, filtered]);

  const detailTotals = useMemo(() => detailRows.reduce((acc, r) => ({
    prix: acc.prix + r.prix, encaisse: acc.encaisse + r.encaisse, encours: acc.encours + r.encours,
  }), { prix: 0, encaisse: 0, encours: 0 }), [detailRows]);

  const exportRows = (list, fmtV = v => v) => list.map(r => {
    const t = blocCells(r.titre), nt = blocCells(r.nonTitre), tot = blocCells(r.total);
    const pctStr = v => v != null ? `${v}%` : '—';
    return {
      'Projet': r.projet,
      'Ventes (titrés)': fmtV(t.ventes), 'Encaissements (titrés)': fmtV(t.encaisse), 'Encours (titrés)': fmtV(t.encours), 'Ratio Encours/Ventes (titrés)': pctStr(t.pctEncrs),
      'Ventes (non titrés)': fmtV(nt.ventes), 'Encaissements (non titrés)': fmtV(nt.encaisse), 'Encours (non titrés)': fmtV(nt.encours), 'Ratio Encours/Ventes (non titrés)': pctStr(nt.pctEncrs),
      'Ventes (total)': fmtV(tot.ventes), 'Encaissements (total)': fmtV(tot.encaisse), 'Encours (total)': fmtV(tot.encours), 'Ratio Encours/Ventes (total)': pctStr(tot.pctEncrs),
    };
  });

  if (loading) return <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>;
  if (!rows.length) return <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: '0.85rem' }}>Aucune vente</div>;

  return (
    <div>
      {/* ── Titre — photo à date ── */}
      {fin && (
        <div style={{ textAlign: 'center', marginBottom: 14 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontSize: '0.92rem', fontWeight: 800, color: '#37474f',
            background: '#f5f4f2', border: '1.5px solid #d4c8bc', borderRadius: 20,
            padding: '6px 20px',
          }}>
            📅 Situation de l'encours jusqu'au <span style={{ color: '#c62828' }}>{fmtDate(fin)}</span>
          </span>
        </div>
      )}

      {/* ── Pills filtre statut notaire ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7b3a9a', textTransform: 'uppercase', letterSpacing: 0.8, marginRight: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          🏛 Statut<InfoTip text="Filtre selon l'avancement du dossier chez le notaire. Signé client = chez_notaire=1 · Signature en cours = en_cours_signature=1 · Signé notaire = bien.STATUT = SIGNE NOTAIRE · Pas encore signé = aucune des conditions précédentes." />
        </span>
        {STATUT_PILLS.map(opt => {
          const active = activeStatuts.has(opt.key);
          return (
            <button key={opt.key} onClick={() => toggleStatut(opt.key)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: '0.71rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                border: `1.5px solid ${active ? '#7b3a9a' : '#d8cef0'}`,
                background: active ? '#7b3a9a' : '#f7f3ff',
                color: active ? '#fff' : '#7b3a9a',
              }}>
              {opt.label}
            </button>
          );
        })}
        {activeStatuts.size > 0 && (
          <button onClick={() => setActiveStatuts(new Set())}
            style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px solid #ccc', color: '#999' }}>
            ✕ Tous
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.67rem', color: '#aaa' }}>
          {tableRows.length} projet{tableRows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Résumé rapide ── */}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 12, padding: '8px 14px', background: '#fafafa', border: '1px solid #ede9e4', borderRadius: 8 }}>
        <span style={{ fontSize: '0.68rem', color: '#888' }}>Ventes <b style={{ color: '#333', fontSize: '0.8rem' }}>{fmtUnit(totalCells.ventes, unit)}</b></span>
        <span style={{ fontSize: '0.68rem', color: '#888' }}>Encaissé <b style={{ color: '#2e7d32', fontSize: '0.8rem' }}>{fmtUnit(totalCells.encaisse, unit)}</b></span>
        <span style={{ fontSize: '0.68rem', color: '#888' }}>Encours <b style={{ color: ratioColor(totalCells.pctEncrs), fontSize: '0.8rem' }}>{fmtUnit(totalCells.encours, unit)}</b> <span style={{ color: ratioColor(totalCells.pctEncrs), fontWeight: 700 }}>({totalCells.pctEncrs != null ? `${totalCells.pctEncrs}%` : '—'})</span></span>
        <TableExportBar style={{ marginLeft: 'auto' }}
          onExcel={() => exportExcel(exportRows(tableRows), 'situation-encours')}
          onCopy={() => { const { html, plain } = rowsToHtml(exportRows(tableRows, v => fmtUnit(v, unit))); copyHtmlToClipboard(html, plain); }}
        />
      </div>

      <div style={{ borderRadius: 8, border: '2px solid #d4c8bc', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...TH, textAlign: 'left', verticalAlign: 'bottom', background: '#4a505c', position: 'sticky', left: 0, zIndex: 2, ...SEP }}>Projet</th>
                <th colSpan={4} style={{ ...TH, background: BLOC_COLORS.titre, ...SEP }}>Lots vendus titrés</th>
                <th colSpan={4} style={{ ...TH, background: BLOC_COLORS.nonTitre, ...SEP }}>Lots vendus non titrés</th>
                <th colSpan={4} style={{ ...TH, background: BLOC_COLORS.total }}>Total</th>
              </tr>
              <tr>
                <th style={{ ...TH, color: '#4a505c', background: '#e3eaf2' }}>Ventes</th><th style={{ ...TH, color: '#4a505c', background: '#e3eaf2' }}>Encaiss.</th><th style={{ ...TH, color: '#4a505c', background: '#e3eaf2' }}>Encours</th><th style={{ ...TH, color: '#4a505c', background: '#e3eaf2', ...SEP }}>Ratio<InfoTip text={RATIO_INFO} /></th>
                <th style={{ ...TH, color: '#4a505c', background: '#fbe9dd' }}>Ventes</th><th style={{ ...TH, color: '#4a505c', background: '#fbe9dd' }}>Encaiss.</th><th style={{ ...TH, color: '#4a505c', background: '#fbe9dd' }}>Encours</th><th style={{ ...TH, color: '#4a505c', background: '#fbe9dd', ...SEP }}>Ratio<InfoTip text={RATIO_INFO} /></th>
                <th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Ventes</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Encaiss.</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Encours</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Ratio<InfoTip text={RATIO_INFO} /></th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, i) => (
                <tr key={r.projetId} style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'}>
                  <td style={{ ...TD, textAlign: 'left', fontWeight: 700, color: '#4a505c', background: 'inherit', position: 'sticky', left: 0, ...SEP }}>{r.projet}</td>
                  <BlocCells bucket={r.titre} unit={unit} onCellClick={(col) => setActiveCell({ projetId: r.projetId, projet: r.projet, dim: 'titre', bucketKey: 'titre', bucketLabel: BUCKET_LABELS.titre, column: col })} />
                  <BlocCells bucket={r.nonTitre} unit={unit} onCellClick={(col) => setActiveCell({ projetId: r.projetId, projet: r.projet, dim: 'titre', bucketKey: 'nonTitre', bucketLabel: BUCKET_LABELS.nonTitre, column: col })} />
                  <BlocCells bucket={r.total} unit={unit} last onCellClick={(col) => setActiveCell({ projetId: r.projetId, projet: r.projet, dim: 'titre', bucketKey: 'total', bucketLabel: BUCKET_LABELS.total, column: col })} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #d4c8bc', background: '#f5f4f2' }}>
                <td style={{ ...TD, textAlign: 'left', fontWeight: 800, color: '#4a505c', background: '#f5f4f2', position: 'sticky', left: 0, ...SEP }}>TOTAL</td>
                <BlocCells bucket={grandTotal.titre} unit={unit} onCellClick={(col) => setActiveCell({ projetId: 'ALL', projet: 'Tous les projets', dim: 'titre', bucketKey: 'titre', bucketLabel: BUCKET_LABELS.titre, column: col })} />
                <BlocCells bucket={grandTotal.nonTitre} unit={unit} onCellClick={(col) => setActiveCell({ projetId: 'ALL', projet: 'Tous les projets', dim: 'titre', bucketKey: 'nonTitre', bucketLabel: BUCKET_LABELS.nonTitre, column: col })} />
                <BlocCells bucket={grandTotal.total} unit={unit} last onCellClick={(col) => setActiveCell({ projetId: 'ALL', projet: 'Tous les projets', dim: 'titre', bucketKey: 'total', bucketLabel: BUCKET_LABELS.total, column: col })} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Complément — disponibilité du titre foncier ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '20px 0 10px' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#37474f', display: 'flex', alignItems: 'center', gap: 3 }}>
          🏷️ Complément — Disponibilité du titre foncier<InfoTip text={TF_INFO} />
        </span>
      </div>

      <div style={{ borderRadius: 8, border: '2px solid #d4c8bc', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...TH, textAlign: 'left', verticalAlign: 'bottom', background: '#4a505c', position: 'sticky', left: 0, zIndex: 2, ...SEP }}>Projet</th>
                {TF_BUCKETS.map(k => (
                  <th key={k} colSpan={4} style={{ ...TH, background: TF_COLORS[k], ...SEP }}>{TF_LABELS[k]}</th>
                ))}
                <th colSpan={4} style={{ ...TH, background: TF_COLORS.total }}>Total</th>
              </tr>
              <tr>
                {TF_BUCKETS.map(k => (
                  <React.Fragment key={k}>
                    <th style={{ ...TH, color: '#4a505c', background: TF_BG[k] }}>Ventes</th>
                    <th style={{ ...TH, color: '#4a505c', background: TF_BG[k] }}>Encaiss.</th>
                    <th style={{ ...TH, color: '#4a505c', background: TF_BG[k] }}>Encours</th>
                    <th style={{ ...TH, color: '#4a505c', background: TF_BG[k], ...SEP }}>Ratio<InfoTip text={RATIO_INFO} /></th>
                  </React.Fragment>
                ))}
                <th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Ventes</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Encaiss.</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Encours</th><th style={{ ...TH, color: '#4a505c', background: '#e6e8ea' }}>Ratio<InfoTip text={RATIO_INFO} /></th>
              </tr>
            </thead>
            <tbody>
              {tableRowsTf.map((r, i) => (
                <tr key={r.projetId} style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'}>
                  <td style={{ ...TD, textAlign: 'left', fontWeight: 700, color: '#4a505c', background: 'inherit', position: 'sticky', left: 0, ...SEP }}>{r.projet}</td>
                  {TF_BUCKETS.map(k => (
                    <BlocCells key={k} bucket={r[k]} unit={unit}
                      onCellClick={(col) => setActiveCell({ projetId: r.projetId, projet: r.projet, dim: 'tf', bucketKey: k, bucketLabel: TF_LABELS[k], column: col })} />
                  ))}
                  <BlocCells bucket={r.total} unit={unit} last
                    onCellClick={(col) => setActiveCell({ projetId: r.projetId, projet: r.projet, dim: 'tf', bucketKey: 'total', bucketLabel: TF_LABELS.total, column: col })} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #d4c8bc', background: '#f5f4f2' }}>
                <td style={{ ...TD, textAlign: 'left', fontWeight: 800, color: '#4a505c', background: '#f5f4f2', position: 'sticky', left: 0, ...SEP }}>TOTAL</td>
                {TF_BUCKETS.map(k => (
                  <BlocCells key={k} bucket={grandTotalTf[k]} unit={unit}
                    onCellClick={(col) => setActiveCell({ projetId: 'ALL', projet: 'Tous les projets', dim: 'tf', bucketKey: k, bucketLabel: TF_LABELS[k], column: col })} />
                ))}
                <BlocCells bucket={grandTotalTf.total} unit={unit} last
                  onCellClick={(col) => setActiveCell({ projetId: 'ALL', projet: 'Tous les projets', dim: 'tf', bucketKey: 'total', bucketLabel: TF_LABELS.total, column: col })} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <TableExportBar
          onExcel={() => exportExcel(exportRowsTf(tableRowsTf), 'situation-encours-tf')}
          onCopy={() => { const { html, plain } = rowsToHtml(exportRowsTf(tableRowsTf, v => fmtUnit(v, unit))); copyHtmlToClipboard(html, plain); }}
        />
      </div>

      {/* ── Modal détail (clic sur une cellule) ── */}
      {activeCell && (
        <div onClick={() => setActiveCell(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 10, width: '100%', maxWidth: 1180, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '12px 18px', background: '#4a505c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{activeCell.projet}</div>
                <div style={{ fontSize: '0.72rem', opacity: 0.8 }}>{activeCell.bucketLabel} — {COLUMN_LABELS[activeCell.column]}</div>
              </div>
              <button onClick={() => setActiveCell(null)}
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
            </div>

            <div style={{ padding: '8px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #ede9e4', flexShrink: 0 }}>
              <span style={{ fontSize: '0.7rem', color: '#888' }}>{detailRows.length} ligne{detailRows.length !== 1 ? 's' : ''}</span>
              <TableExportBar style={{ marginLeft: 'auto' }}
                onExcel={() => exportExcel(detailRows.map(r => ({
                  'ID Contrat': r.contratId, 'Date': r.dateReserv, 'N° Dossier': r.numDoss, 'Description': r.description,
                  'Type': r.type, 'Tranche': r.tranche, 'Superficie': r.superficie, 'Prix/m²': r.prixM,
                  'Part': r.part != null ? `${r.part}%` : '', 'Commercial': r.commercial, 'Client(s)': r.clients,
                  'Statut notaire': STATUT_LABELS[r.statutNotaire] || r.statutNotaire,
                  'Prix': r.prix, 'Encaissé': r.encaisse, 'Encours': r.encours,
                })), 'situation-encours-detail')}
              />
            </div>

            <div style={{ overflow: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>ID</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Date</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>N° Dossier</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Description</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Type</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Tranche</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Sup. m²</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Part</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Commercial</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Client(s)</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', textAlign: 'left', position: 'sticky', top: 0 }}>Statut</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Prix</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Encaissé</th>
                    <th style={{ ...TH, color: '#4a505c', background: '#f5f4f2', position: 'sticky', top: 0 }}>Encours</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.length === 0 && (
                    <tr><td colSpan={14} style={{ ...TD, textAlign: 'center', color: '#aaa', padding: 24 }}>Aucune ligne</td></tr>
                  )}
                  {detailRows.map((r, i) => (
                    <tr key={`${r.rowType}-${r.id}`} style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}>
                      <td style={{ ...TD, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        <a href={`https://shems.ngrok.io/consultation/${r.contratId}`} target="_blank" rel="noreferrer"
                          style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>#{r.contratId}</a>
                      </td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(r.dateReserv)}</td>
                      <td style={{ ...TD, textAlign: 'left', color: '#6d4e36', fontWeight: 600 }}>{r.numDoss || '—'}</td>
                      <td style={{ ...TD, textAlign: 'left', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || '—'}</td>
                      <td style={{ ...TD, textAlign: 'left' }}>
                        {r.rowType === 'association'
                          ? <span style={{ fontSize: '0.65rem', fontWeight: 700, background: '#e3f2fd', color: '#1565c0', borderRadius: 8, padding: '2px 7px' }}>Asso. {r.type || ''}</span>
                          : (r.type || '—')}
                      </td>
                      <td style={{ ...TD, textAlign: 'left' }}>{r.tranche || '—'}</td>
                      <td style={TD}>{r.superficie != null ? fmt(r.superficie) : '—'}</td>
                      <td style={{ ...TD, color: '#1565c0' }}>{r.part != null ? `${r.part}%` : '—'}</td>
                      <td style={{ ...TD, textAlign: 'left' }}>{r.commercial || '—'}</td>
                      <td style={{ ...TD, textAlign: 'left', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.clients || '—'}</td>
                      <td style={{ ...TD, textAlign: 'left' }}>{STATUT_LABELS[r.statutNotaire] || r.statutNotaire}</td>
                      <td style={TD}>{fmtUnit(r.prix, unit)}</td>
                      <td style={{ ...TD, color: '#2e7d32' }}>{fmtUnit(r.encaisse, unit)}</td>
                      <td style={{ ...TD, color: r.encours > 0 ? '#c62828' : '#888' }}>{fmtUnit(r.encours, unit)}</td>
                    </tr>
                  ))}
                </tbody>
                {detailRows.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#f5f4f2', fontWeight: 700, borderTop: '2px solid #d4c8bc' }}>
                      <td colSpan={11} style={{ ...TD, textAlign: 'left' }}>TOTAL</td>
                      <td style={TD}>{fmtUnit(detailTotals.prix, unit)}</td>
                      <td style={{ ...TD, color: '#2e7d32' }}>{fmtUnit(detailTotals.encaisse, unit)}</td>
                      <td style={{ ...TD, color: '#c62828' }}>{fmtUnit(detailTotals.encours, unit)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SituationEncoursSection;
